/**
 * security.js
 * ───────────
 * Lysarq Client-Side Anti-Scraping & Fingerprinting Engine.
 * 
 * Provides:
 *  1. Ephemeral Client Cryptographic Signatures (Method + Path + Query + Body + Nonce)
 *  2. Headless / WebDriver / Automation detection (Puppeteer, Selenium, Playwright)
 *  3. Behavioral pointer physics & input integrity tracking
 *  4. Automated background telemetry reporting
 * 
 * NOTE: Cryptographic client material is considered inspectable by an attacker.
 * These signatures act as an anti-replay / anti-abuse signal and are NEVER
 * a replacement for server-side authentication and authorization.
 */

const SECURITY_SALT = 'lysarq-msme-anti-scraping-salt-2026';

/**
 * Generate a cryptographically strong random nonce
 */
function generateNonce() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  const array = new Uint8Array(16);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }
  return 'nonce_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Sorts query parameters into a deterministic canonical string
 */
function canonicalizeQuery(queryString) {
  if (!queryString) return '';
  const cleanQuery = queryString.startsWith('?') ? queryString.slice(1) : queryString;
  if (!cleanQuery) return '';
  const params = new URLSearchParams(cleanQuery);
  const entries = Array.from(params.entries());
  entries.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  return new URLSearchParams(entries).toString();
}

/**
 * Computes SHA-256 hash of body content
 */
async function computeBodyHash(body) {
  if (!body) return '';
  try {
    const text = typeof body === 'string' ? body : JSON.stringify(body);
    if (window.crypto && window.crypto.subtle) {
      const msgBuffer = new TextEncoder().encode(text);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 16);
    }
  } catch (e) {
    // Fallback
  }
  return '';
}

/**
 * Computes canonical signature binding Method, Path, CanonicalQuery, BodyHash, Timestamp, Nonce
 */
async function computeCanonicalSignature(method, path, canonicalQuery, bodyHash, timestamp, nonce) {
  const message = `${method.toUpperCase()}|${path}|${canonicalQuery}|${bodyHash}|${timestamp}|${nonce}`;
  try {
    if (window.crypto && window.crypto.subtle) {
      const keyBuffer = new TextEncoder().encode(SECURITY_SALT);
      const msgBuffer = new TextEncoder().encode(message);
      const key = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      const signatureBuffer = await window.crypto.subtle.sign('HMAC', key, msgBuffer);
      const sigArray = Array.from(new Uint8Array(signatureBuffer));
      return sigArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 24);
    }
  } catch (e) {
    // SubtleCrypto HMAC unavailable fallback
  }

  // Pure JS fallback
  let hash = 2166136261;
  const str = `${message}:${SECURITY_SALT}`;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(16, '0');
}

/**
 * Inspect browser environment for automation signatures & headless flags.
 * Returns array of triggered detection flags.
 */
export function detectBrowserAutomation() {
  const flags = [];

  try {
    // 1. Standard WebDriver flag
    if (navigator.webdriver === true) {
      flags.push('NAVIGATOR_WEBDRIVER_ACTIVE');
    }

    // 2. Chromedriver specific variables injected into global window
    const cdcProps = Object.getOwnPropertyNames(window).filter(p => p.startsWith('cdc_') || p.includes('cdc_adoQpoasnfa'));
    if (cdcProps.length > 0) {
      flags.push('CHROMEDRIVER_CDC_ARTIFACT');
    }

    // 3. Selenium & Puppeteer global runtime markers
    if (
      window.__puppeteer_evaluation_script__ ||
      window.__nightmare ||
      window._phantom ||
      window.callPhantom ||
      document.__selenium_unwrapped ||
      document.__webdriver_evaluate
    ) {
      flags.push('HEADLESS_FRAMEWORK_RUNTIME');
    }

    // 4. Abnormal window dimensions (0x0 or screen dimension mismatch)
    if (window.outerWidth === 0 && window.outerHeight === 0) {
      flags.push('ZERO_VIEWPORT_DIMENSIONS');
    }

    // 5. Chrome runtime discrepancy (User agent says Chrome, but chrome window object missing)
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome && !window.chrome) {
      flags.push('CHROME_WINDOW_OBJECT_MISSING');
    }

    // 6. User Agent contains headless keywords
    if (/HeadlessChrome|Electron|PhantomJS|Wget|Scrapy|Selenium/i.test(navigator.userAgent)) {
      flags.push('AUTOMATION_USER_AGENT_STRING');
    }
  } catch (err) {
    // Ignore detection errors in restricted environments
  }

  return flags;
}

/**
 * Generate security headers binding request properties with replay protection
 * @param {string} [method='GET']
 * @param {string} [url='']
 * @param {any} [body=null]
 */
export async function getClientSecurityHeaders(method = 'GET', url = '', body = null) {
  const timestamp = Date.now();
  const nonce = generateNonce();

  let pathname = url;
  let queryString = '';
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      pathname = parsed.pathname;
      queryString = parsed.search;
    } else {
      const parts = url.split('?');
      pathname = parts[0];
      queryString = parts[1] || '';
    }
  } catch (e) {
    pathname = url;
  }

  const canonicalQuery = canonicalizeQuery(queryString);
  const bodyHash = await computeBodyHash(body);
  const signature = await computeCanonicalSignature(
    method,
    pathname,
    canonicalQuery,
    bodyHash,
    String(timestamp),
    nonce
  );

  return {
    'X-Client-Timestamp': String(timestamp),
    'X-Client-Nonce': nonce,
    'X-Client-Signature': signature,
    'X-Client-Platform': 'lysarq-web-bi',
  };
}

/**
 * Report suspicious browser automation signals to backend telemetry
 */
let _telemetryReported = false;
export async function reportSecurityTelemetry(apiUrl) {
  if (_telemetryReported) return;
  const flags = detectBrowserAutomation();
  if (flags.length === 0) return;

  _telemetryReported = true;
  try {
    const endpoint = `${apiUrl}/security/telemetry/report`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, JSON.stringify({ flags, timestamp: Date.now() }));
    } else {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flags, timestamp: Date.now() }),
      }).catch(() => {});
    }
  } catch (e) {
    // Fail silently
  }
}
